import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { Preferences } from '@capacitor/preferences'
import {
    hasAdminPassword,
    hasAdminGodPassword,
    validateAdminPassword,
    validateAdminGodPassword,
    setAdminPassword,
    setAdminGodPassword,
    createAdminSession,
    getAdminSession,
    clearAdminSession,
    extendAdminSession,
    recordFailedAttempt,
    clearFailedAttempts,
    isLockedOut,
    getFailedAttempts,
    getRemainingLockoutTime,
    clearAllAdminData
} from '@/services/storage/admin-storage'
import { AdminAccessLevel } from '@/types/admin'

export interface DebugSettings {
    bypassGeofence: boolean
    allowStaticQR: boolean
    lastModified: string
}

export interface AdminSettings {
    debugMode: boolean
    bypassGeofence: boolean
    bypassQrTimestamp: boolean
    showInternalLogs: boolean
}

export interface LogEntry {
    id: string
    timestamp: string
    level: 'info' | 'warn' | 'error'
    category: string
    message: string
    metadata?: Record<string, unknown>
}

export interface AdminAuthState {
    isAuthenticated: boolean
    sessionExpiry: number
    failedAttempts: number
    lastAttemptTime: number
}

const DEBUG_SETTINGS_KEY = 'debug_settings'

export const useAdminStore = defineStore('admin', () => {
    const settings = ref<AdminSettings>({
        debugMode: false,
        bypassGeofence: false,
        bypassQrTimestamp: false,
        showInternalLogs: false
    })

    const debugSettings = ref<DebugSettings>({
        bypassGeofence: false,
        allowStaticQR: false,
        lastModified: ''
    })

    const logs = ref<LogEntry[]>([])
    const isAdminUnlocked = ref(false)

    // Authentication state
    const isAuthenticated = ref(false)
    const sessionExpiry = ref(0)
    const failedAttempts = ref(0)
    const lastAttemptTime = ref(0)
    const isPasswordSet = ref(false)
    const isGodPasswordSet = ref(false)
    const lockedOut = ref(false)
    const lockoutTimeRemaining = ref(0)
    const accessLevel = ref<AdminAccessLevel>(AdminAccessLevel.NONE)

    // Gesture state management
    const tapCount = ref(0)
    const lastTapTime = ref(0)
    const sessionStartTime = ref<number | null>(null)

    /**
     * Syncs the admin passwords with the environment variables
     * This ensures the passwords always match the .env file
     */
    async function syncPasswordWithEnv(): Promise<void> {
        const { env } = await import('@/utils/env')

        if (env.adminPassword) {
            await setAdminPassword(env.adminPassword)
            isPasswordSet.value = true
        }

        if (env.adminGodPassword) {
            await setAdminGodPassword(env.adminGodPassword)
            isGodPasswordSet.value = true
        }
    }

    /**
     * Initialize admin authentication state from storage
     * Always syncs passwords with environment variables to ensure consistency
     */
    async function initializeAuth(): Promise<void> {
        // Always sync passwords with environment variables
        await syncPasswordWithEnv()

        // Check if passwords are set
        isPasswordSet.value = await hasAdminPassword()
        isGodPasswordSet.value = await hasAdminGodPassword()

        // Check if locked out
        lockedOut.value = await isLockedOut()

        if (lockedOut.value) {
            lockoutTimeRemaining.value = await getRemainingLockoutTime()
        }

        // Load failed attempts
        const attemptData = await getFailedAttempts()
        failedAttempts.value = attemptData.count
        lastAttemptTime.value = attemptData.lastAttemptTime

        // NOTE: We do NOT restore sessions anymore - user must authenticate every time
        // Clear any existing session when initializing
        await clearAdminSession()
        isAuthenticated.value = false
        accessLevel.value = AdminAccessLevel.NONE

        // IMPORTANT: Also ensure isAdminUnlocked is not persisted from previous session
        // The gesture state should be managed separately, but we need to ensure
        // authentication is not bypassed
        // Note: We don't reset isAdminUnlocked here as it's controlled by the gesture

        // Load debug settings
        await loadDebugSettings()
    }

    /**
     * Sets up the admin password (first-time setup or updates from env)
     */
    async function setupPassword(password: string): Promise<void> {
        if (password.length < 6) {
            throw new Error('Password must be at least 6 characters')
        }

        await setAdminPassword(password)
        isPasswordSet.value = true
    }

    /**
     * Authenticates admin with password
     * Checks both normal and god mode passwords
     */
    async function authenticate(password: string): Promise<boolean> {
        // Check if locked out
        if (await isLockedOut()) {
            lockedOut.value = true
            lockoutTimeRemaining.value = await getRemainingLockoutTime()
            throw new Error('Too many failed attempts. Please try again later.')
        }

        // Check god mode password first (higher privilege)
        const isGodValid = await validateAdminGodPassword(password)

        if (isGodValid) {
            // Successful god mode authentication
            await clearFailedAttempts()
            await createAdminSession(AdminAccessLevel.GOD)

            isAuthenticated.value = true
            // Note: Do NOT set isAdminUnlocked here - that's controlled by the gesture only
            sessionStartTime.value = Date.now()
            failedAttempts.value = 0
            lockedOut.value = false
            lockoutTimeRemaining.value = 0
            accessLevel.value = AdminAccessLevel.GOD

            const session = await getAdminSession()
            if (session) {
                sessionExpiry.value = session.expiryTime
            }

            return true
        }

        // Check normal admin password
        const isNormalValid = await validateAdminPassword(password)

        if (isNormalValid) {
            // Successful normal authentication
            await clearFailedAttempts()
            await createAdminSession(AdminAccessLevel.NORMAL)

            isAuthenticated.value = true
            // Note: Do NOT set isAdminUnlocked here - that's controlled by the gesture only
            sessionStartTime.value = Date.now()
            failedAttempts.value = 0
            lockedOut.value = false
            lockoutTimeRemaining.value = 0
            accessLevel.value = AdminAccessLevel.NORMAL

            const session = await getAdminSession()
            if (session) {
                sessionExpiry.value = session.expiryTime
            }

            return true
        } else {
            // Failed authentication
            const attemptData = await recordFailedAttempt()
            failedAttempts.value = attemptData.count
            lastAttemptTime.value = attemptData.lastAttemptTime

            if (attemptData.lockoutUntil) {
                lockedOut.value = true
                lockoutTimeRemaining.value = attemptData.lockoutUntil - Date.now()
            }

            return false
        }
    }

    /**
     * Logs out the admin and clears session
     */
    async function logout(): Promise<void> {
        await clearAdminSession()
        isAuthenticated.value = false
        isAdminUnlocked.value = false
        sessionStartTime.value = null
        sessionExpiry.value = 0
        accessLevel.value = AdminAccessLevel.NONE
    }

    /**
     * Extends the current admin session
     */
    async function extendSession(): Promise<void> {
        if (isAuthenticated.value) {
            await extendAdminSession()
            const session = await getAdminSession()
            if (session) {
                sessionExpiry.value = session.expiryTime
            }
        }
    }

    /**
     * Checks if the current session is still valid
     */
    async function validateSession(): Promise<boolean> {
        const session = await getAdminSession()

        if (!session) {
            isAuthenticated.value = false
            isAdminUnlocked.value = false
            accessLevel.value = AdminAccessLevel.NONE
            return false
        }

        isAuthenticated.value = true
        sessionExpiry.value = session.expiryTime
        accessLevel.value = session.accessLevel
        return true
    }

    /**
     * Resets admin password (clears all admin data)
     */
    async function resetPassword(): Promise<void> {
        await clearAllAdminData()

        // Reset all state
        isPasswordSet.value = false
        isGodPasswordSet.value = false
        isAuthenticated.value = false
        isAdminUnlocked.value = false
        sessionStartTime.value = null
        sessionExpiry.value = 0
        failedAttempts.value = 0
        lastAttemptTime.value = 0
        lockedOut.value = false
        lockoutTimeRemaining.value = 0
        accessLevel.value = AdminAccessLevel.NONE
    }

    function updateSettings(newSettings: Partial<AdminSettings>) {
        settings.value = { ...settings.value, ...newSettings }
    }

    function addLog(log: Omit<LogEntry, 'id'>) {
        logs.value.push({
            ...log,
            id: crypto.randomUUID()
        })
    }

    function clearLogs() {
        logs.value = []
    }

    function unlockAdmin() {
        isAdminUnlocked.value = true
        sessionStartTime.value = Date.now()
    }

    function lockAdmin() {
        isAdminUnlocked.value = false
        sessionStartTime.value = null
    }

    // Gesture management methods
    function incrementTapCount() {
        const now = Date.now()

        // Reset if more than 2 seconds have passed since last tap
        if (now - lastTapTime.value > 2000) {
            tapCount.value = 0
        }

        tapCount.value++
        lastTapTime.value = now

        // Check if 5 taps reached
        if (tapCount.value >= 5) {
            unlockAdmin()
            tapCount.value = 0
        }
    }

    function resetTapCount() {
        tapCount.value = 0
        lastTapTime.value = 0
    }

    // Debug settings management
    async function loadDebugSettings(): Promise<void> {
        try {
            const { value } = await Preferences.get({ key: DEBUG_SETTINGS_KEY })
            if (value) {
                const stored: DebugSettings = JSON.parse(value)
                debugSettings.value = stored
            }
        } catch (error) {
            console.error('Failed to load debug settings', error)
        }
    }

    async function saveDebugSettings(): Promise<void> {
        try {
            debugSettings.value.lastModified = new Date().toISOString()
            await Preferences.set({
                key: DEBUG_SETTINGS_KEY,
                value: JSON.stringify(debugSettings.value)
            })
        } catch (error) {
            console.error('Failed to save debug settings', error)
            throw error
        }
    }

    async function updateDebugSettings(updates: Partial<DebugSettings>): Promise<void> {
        debugSettings.value = { ...debugSettings.value, ...updates }
        await saveDebugSettings()
    }

    async function clearDebugSettings(): Promise<void> {
        debugSettings.value = {
            bypassGeofence: false,
            allowStaticQR: false,
            lastModified: ''
        }
        await Preferences.remove({ key: DEBUG_SETTINGS_KEY })
    }

    // Computed properties
    const isAdminAccessible = computed(() => tapCount.value >= 5 || isAdminUnlocked.value)
    const sessionTimeRemaining = computed(() => {
        if (!isAuthenticated.value) return 0
        const remaining = sessionExpiry.value - Date.now()
        return remaining > 0 ? remaining : 0
    })
    const isDebugModeActive = computed(
        () => debugSettings.value.bypassGeofence || debugSettings.value.allowStaticQR
    )
    const hasGodMode = computed(() => accessLevel.value === AdminAccessLevel.GOD)

    return {
        settings,
        debugSettings,
        logs,
        isAdminUnlocked,
        isAuthenticated,
        isPasswordSet,
        isGodPasswordSet,
        sessionExpiry,
        failedAttempts,
        lastAttemptTime,
        lockedOut,
        lockoutTimeRemaining,
        tapCount,
        lastTapTime,
        sessionStartTime,
        accessLevel,
        isAdminAccessible,
        sessionTimeRemaining,
        isDebugModeActive,
        hasGodMode,
        initializeAuth,
        setupPassword,
        authenticate,
        syncPasswordWithEnv,
        logout,
        extendSession,
        validateSession,
        resetPassword,
        updateSettings,
        addLog,
        clearLogs,
        unlockAdmin,
        lockAdmin,
        incrementTapCount,
        resetTapCount,
        loadDebugSettings,
        saveDebugSettings,
        updateDebugSettings,
        clearDebugSettings
    }
})
